import HoldingsHeader from './HoldingsHeader';
import HoldingsRow from './HoldingsRow';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getHoldings } from '../../services/apis/holdings';
import { setHoldings, Holding, setHoldingsLoading, setHoldingsError, setPrevHoldingsTradingAccountId } from '../../reducer/holdings.reducer';
import { Paper, Typography, Box, Grid, Divider } from '@mui/material'
import { Security, SecuritySnapshots } from '../../proto/generated/security.ts'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles.ts'
import websocketActions from '../../actions/websocket.actions.ts'
import { subscriptionTags } from '../../assets/constants.ts'
import Holdings from '../../assets/icons/Holdings.tsx'
import { EmptyState, GhostRows } from '../common/EmptyState';
import NoTradingAccountEmptyState from '../tradingAccountStatus/NoTradingAccountEmptyState';
import ListSkeleton from '../common/ListSkeleton';
import { TradingAccountStatus } from '../../proto/generated/trading-bridge.ts';
import { getOrderBooksBulk } from '../../util/tradepanel.ts';
import { OrderType, TransactionType, Product, Broker_OrderType } from '../../proto/generated/order'
import { setQuickOrderData } from '../../reducer/orders.reducer';
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector.tsx';
import { selectPrimaryTradingAccount } from '../../selectors/user.ts';
import { selectHoldings, selectHoldingsLoaded, selectHoldingsLoading, selectHoldingsPrevTradingAccount } from '../../selectors/holdings.ts';
import { updateSnapshots } from '../../reducer/security.reducer';
import { getHoldingsSecurityKeys } from '../../util/positionLtpsSignature';
import { RootState } from '../../store';
import { useTradingAccountContext } from '../../hooks/useTradingAccountContext';
import { isPaperBrokerCode } from '../../util/tradingAccounts';

type HoldingsContentProps = {
    holdings: Holding[];
    onSquareOff: (holding: Holding) => void;
};

/** Subscribes to snapshot presence only — not LTP ticks — so the list shell stays stable. */
const HoldingsContent = memo(({ holdings, onSquareOff }: HoldingsContentProps) => {
    const securityKeys = useMemo(() => getHoldingsSecurityKeys(holdings), [holdings]);
    const snapshotsReady = useSelector((state: RootState) =>
        securityKeys.length > 0 && securityKeys.every((key) => !!state.securityStore.snapshots[key])
    );

    if (!snapshotsReady) {
        return <ListSkeleton variant="holdings" />;
    }

    return (
        <>
            <HoldingsHeader holdings={holdings} />
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {holdings.map((holding) => (
                    <HoldingsRow
                        holding={holding}
                        key={holding.security?.code}
                        onSquareOff={onSquareOff}
                    />
                ))}
            </Box>
        </>
    );
}, (prev, next) => prev.holdings === next.holdings && prev.onSquareOff === next.onSquareOff);

const HoldingsWidget = () => {
    const dispatch = useDispatch();
    const holdings = useSelector(selectHoldings)
    const loading = useSelector(selectHoldingsLoading)
    const loaded = useSelector(selectHoldingsLoaded)
    const prevTradingAccountRef = useSelector(selectHoldingsPrevTradingAccount)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount);
    const { mode, practiceEnabled } = useTradingAccountContext();
    const prevHoldingsTradingAccount = useRef<string | null>(null);

    const inactiveAccountEmptyText = useMemo(() => {
        if (!primaryTradingAccount) {
            return undefined;
        }
        if (isPaperBrokerCode(primaryTradingAccount.brokerCode) && !practiceEnabled) {
            return 'Enable practice mode to view holdings.';
        }
        return 'Holdings will appear once your trading account is active.';
    }, [practiceEnabled, primaryTradingAccount]);

    const fetchSecuritySnapshotBulk = useCallback(
        async (securities: Security[]) => {
            try {
                const result: SecuritySnapshots = await fetchSecuritySnapshotsBulk({
                    securities: Array.from(securities),
                    dispatch: dispatch
                })
                const noPrevCloseSecurities = result.securitySnapshots.filter(snap => (snap.previousClose === 0 || snap.previousClose === undefined) && snap.security !== undefined).map(s => s.security)
                if (noPrevCloseSecurities.length > 0) {
                    const marketDepth = await getOrderBooksBulk(noPrevCloseSecurities)
                    const enrichedSnapshots = result.securitySnapshots.map((snap) => {
                        if (!snap.security) return snap;
                        const md = marketDepth.find(item => item.security?.code === snap.security?.code && item.security?.exchange === snap.security?.exchange)
                        if (snap && (snap.previousClose === 0 || snap.previousClose === undefined) && md?.close !== undefined) {
                            return { ...snap, previousClose: md.close }
                        }
                        return snap
                    })
                    dispatch(updateSnapshots(enrichedSnapshots))
                }
            } catch (error) {
                console.error('Error fetching security data', error)
            }
        },
        [dispatch]
    )

    const holdingSecurities = useMemo(() => {
        return holdings.map((holding) => holding.security).filter((security): security is Security => security !== undefined)
    }, [holdings])

    useEffect(() => {
        if (holdingSecurities.length > 0) {
            fetchSecuritySnapshotBulk(holdingSecurities)
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.HOLDINGS_WIDGET_SUBSCRIPTION_TAG,
                        securities: holdingSecurities.map((s) => ({ exchange: s.exchange, code: s.code }))
                    }
                ]
            })
        }
    }, [dispatch, holdingSecurities, fetchSecuritySnapshotBulk])

    useEffect(() => {
        if (!primaryTradingAccount) {
            dispatch(setHoldings([]));
            dispatch(setPrevHoldingsTradingAccountId({ id: null, status: null }))
        } else if (primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE) {
            dispatch(setHoldings([]));
        }

        if (
            primaryTradingAccount?.status === TradingAccountStatus.ACTIVE &&
            (prevTradingAccountRef.id !== primaryTradingAccount?.accountId ||
                prevTradingAccountRef.status !== TradingAccountStatus.ACTIVE)
        ) {
            if (prevHoldingsTradingAccount.current !== primaryTradingAccount?.accountId) {
                (async () => {
                    dispatch(setHoldings([]));
                    dispatch(setHoldingsLoading());
                    try {
                        const data: Holding[] = await getHoldings(primaryTradingAccount?.accountId);
                        prevHoldingsTradingAccount.current = primaryTradingAccount?.accountId ?? null;
                        dispatch(setHoldings(data));
                    } catch (err: any) {
                        dispatch(setHoldingsError(err.message));
                    }
                })();
            }
        }
        dispatch(setPrevHoldingsTradingAccountId({ id: primaryTradingAccount?.accountId, status: primaryTradingAccount?.status }))
    }, [dispatch, primaryTradingAccount, prevTradingAccountRef.id, prevTradingAccountRef.status])

    const handleSquareOff = useCallback((holding: Holding) => {
        const transactionType = holding.quantity > 0 ? TransactionType.SELL : TransactionType.BUY;
        if (primaryTradingAccount.accountId) {
            dispatch(setQuickOrderData({
                openDialog: true,
                mode: 'regular',
                type: 'trade',
                isSquareOffOrder: true,
                maxQuantity: holding.quantity,
                order: {
                    transactionType,
                    security: holding.security,
                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                    orderType: OrderType.NORMAL,
                    product: Product.CNC,
                    price: undefined,
                    quantity: holding.quantity,
                }
            }));
        }
    }, [dispatch, primaryTradingAccount]);

    return (
        <Paper
            sx={{
                borderTopRightRadius: '6px',
                display: 'flex',
                height: '100%',
                flexDirection: 'column'
            }}
        >
            <Grid container px={'8px'} py={0} justifyContent={'space-between'} alignItems={'center'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Holdings sx={{ color: (theme) => theme.palette.text.secondary }} />
                    <Typography variant={'body1'}>Holdings</Typography>
                </Box>
                <TradingAccountSelector />
            </Grid>
            <Divider style={{ width: '100%' }} />
            {primaryTradingAccount?.status === TradingAccountStatus.ACTIVE ? (loading || !loaded) ? (
                <ListSkeleton variant="holdings" />
            ) : (holdings.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmptyState preview={<GhostRows variant="holdings" />} text="Your delivery holdings will appear here." />
                </Box>
            ) : (
                <HoldingsContent holdings={holdings} onSquareOff={handleSquareOff} />
            )) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NoTradingAccountEmptyState subject="holdings" preview="holdings" text={inactiveAccountEmptyText} />
                </Box>
            )}
        </Paper>
    );
};

export default memo(HoldingsWidget);
